import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { cancelAccountDeletion, getDeletionStatus, isAuthenticated } from '../../services/authService';

/**
 * ReclaimAccountPage — SCRUM-2266 (MVP 1.21.11.4)
 *
 * Reached from the link in the deletion-confirmation email. Re-authentication
 * is enforced upstream: cancelAccountDeletion() hits a @RequireAuth() endpoint,
 * so an unauthenticated user is redirected to /login first and lands back here.
 *
 * Route: /account/cancel-deletion?code=...   (link form used by Delete-B email)
 *        /account/reclaim?code=...           (alias)
 */
export default function ReclaimAccountPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') || '';

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      // Bounce through login first; come back here afterwards.
      const here = `${window.location.pathname}${window.location.search}`;
      navigate(`/login?redirect=${encodeURIComponent(here)}`);
      return;
    }
    getDeletionStatus()
      .then((result) => setStatus(result))
      .catch(() => setError('Could not load your deletion request.'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleReclaim = async () => {
    setError('');
    setSubmitting(true);
    try {
      await cancelAccountDeletion();
      setSuccess(true);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not cancel deletion. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canReclaim = status && status.status === 'requested';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-md w-full space-y-6">
          <h1 className="text-3xl font-bold text-gray-900">Reclaim your account</h1>
          {code && (
            <p className="text-xs text-gray-400">Reference: {code.slice(0, 12)}…</p>
          )}

          {loading && <p className="text-gray-500">Loading…</p>}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {!loading && !success && !error && !status && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-gray-700 text-sm">You do not have an active deletion request.</p>
            </div>
          )}

          {!loading && !success && status && canReclaim && (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                <p className="text-gray-700 text-sm">
                  Your account is currently scheduled for deletion. Cancelling now keeps your
                  account active — your data will not be removed.
                </p>
              </div>
              <button
                type="button"
                onClick={handleReclaim}
                disabled={submitting}
                className="w-full px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Cancelling…' : 'Keep my account'}
              </button>
            </div>
          )}

          {!loading && !success && status && !canReclaim && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-900 text-sm">
                Deletion has already started ({status.status}). Reclaim is only available during
                the 30-day grace period.
              </p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
              <h2 className="text-xl font-semibold text-green-900">Welcome back</h2>
              <p className="text-green-900 text-sm">Your account has been restored.</p>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Continue to the site
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
